import { z } from "zod";
import { CountryCode } from "../common/country.js";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { PageQuery, page } from "../common/pagination.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
/**
 * Reviews (REV-1, card 092; the owner's rules of 2026-10-04): the buyer reviews a completed order once, within 90 days
 * of completion; the seller replies once and may edit the reply; operators hide a review that breaks the content
 * rules, with a reason both enterprises see. Public pages show published reviews with the buyer's country and
 * industry, and its name only if it chose to show it. Hiding takes a category; for a legal requirement whose reason
 * the law forbids saying, both enterprises read a generic reason instead (card 126).
 */
const Rating = z.number().int().min(1).max(5);
/** Why an operator hid a review (card 126). */
export const ReviewHidingCategory = z
    .enum(["abuse", "personal_data", "unrelated", "legal_requirement", "online_harm"])
    .meta({ id: "ReviewHidingCategory" });
export const ReviewRatings = z
    .object({ quality: Rating, communication: Rating, delivery: Rating })
    .meta({ id: "ReviewRatings" });
export const ReviewBuyer = z
    .object({
    country: CountryCode,
    industry: z.string().nullable(),
    name: LocalizedText.nullable().describe("Only when the buyer chose to show its name"),
})
    .meta({ id: "ReviewBuyer" });
export const ReviewView = z
    .object({
    id: Id,
    orderId: Id,
    side: z.enum(["buyer", "seller"]).describe("The caller's side"),
    ratings: ReviewRatings,
    products: z.array(z.object({ productId: Id, rating: Rating })),
    text: z.string().nullable(),
    buyer: ReviewBuyer,
    reply: z.string().nullable(),
    repliedAt: Timestamp.nullable(),
    status: z.enum(["published", "hidden"]),
    hiddenReason: z
        .string()
        .nullable()
        .describe("Why an operator hid it; both enterprises see it. Null for them when the law forbids saying why (`hiddenReasonWithheld`); operators always read it"),
    hiddenCategory: ReviewHidingCategory.nullable(),
    hiddenReasonWithheld: z
        .boolean()
        .describe("Hidden for a legal requirement whose reason the law forbids saying: show the generic reason"),
    createdAt: Timestamp,
    version: z.number().int().positive(),
    actions: z.array(z.enum(["reply"])).describe("`reply` for the shop's people who reply to reviews"),
})
    .meta({ id: "ReviewView" });
export const PublicReview = z
    .object({
    id: Id,
    ratings: ReviewRatings,
    text: z.string().nullable(),
    buyer: ReviewBuyer,
    reply: z.string().nullable(),
    repliedAt: Timestamp.nullable(),
    createdAt: Timestamp,
})
    .meta({ id: "PublicReview" });
const Party = { surface: "tenant", auth: "user" };
const OrderId = z.object({ orderId: Id });
export const createReview = defineRoute({
    ...Party,
    permission: "review:create",
    id: "trade.reviews.create",
    method: "POST",
    path: "/v1/orders/{orderId}/review",
    marks: ["tenant", "idem"],
    summary: "The buyer reviews a completed order once, within 90 days of completion: quality, communication, and delivery, and every product on the order, from 1 to 5, with optional text. Published at once; never edited",
    prd: ["REV-1"],
    request: {
        params: OrderId,
        body: z.object({
            ratings: ReviewRatings,
            products: z.array(z.object({ productId: Id, rating: Rating })).max(50),
            comment: z.string().trim().min(1).max(2000).optional().describe("The review's text, for other buyers"),
            showName: z
                .boolean()
                .default(false)
                .describe("Show the enterprise's name instead of its country and industry"),
        }),
    },
    responses: { 201: { description: "The review", schema: ReviewView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "WINDOW_CLOSED",
    ],
});
export const getOrderReview = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.reviews.of-order",
    method: "GET",
    path: "/v1/orders/{orderId}/review",
    marks: ["tenant"],
    summary: "An order's review, for either party; `404` while there is none",
    prd: ["REV-1"],
    request: { params: OrderId },
    responses: { 200: { description: "The review", schema: ReviewView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const replyToReview = defineRoute({
    ...Party,
    permission: "review:reply",
    id: "trade.reviews.reply",
    method: "PUT",
    path: "/v1/reviews/{reviewId}/reply",
    marks: ["tenant"],
    summary: "The seller's public reply to a review, written once and editable; the buyer is told the first time",
    prd: ["REV-1"],
    request: {
        params: z.object({ reviewId: Id }),
        body: z.object({ reply: z.string().trim().min(1).max(2000) }),
    },
    responses: { 200: { description: "The review", schema: ReviewView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "VERSION_CONFLICT",
    ],
});
export const listShopReviews = defineRoute({
    ...Party,
    permission: "review:reply",
    id: "trade.reviews.of-shop",
    method: "GET",
    path: "/v1/shop/reviews",
    marks: ["tenant"],
    summary: "The shop's reviews, newest first, hidden ones too with their reason",
    prd: ["REV-1"],
    request: { query: PageQuery },
    responses: { 200: { description: "Reviews", schema: page(ReviewView) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const listPublicShopReviews = defineRoute({
    surface: "public",
    auth: "none",
    permission: "none",
    marks: [],
    id: "catalog.public.shops.reviews",
    method: "GET",
    path: "/v1/public/shops/{slug}/reviews",
    summary: "A selling shop's published reviews, newest first, with the seller's replies",
    prd: ["REV-1", "WEB-1"],
    request: {
        params: z.object({ slug: z.string().regex(/^[a-z0-9-]{1,120}$/) }),
        query: PageQuery,
    },
    responses: { 200: { description: "Reviews", schema: page(PublicReview) } },
    errors: ["NOT_FOUND", "RATE_LIMITED", "VALIDATION_FAILED"],
});
const Ops = { surface: "operator", auth: "operator", permission: "ops:catalog.manage" };
export const getOpsOrderReview = defineRoute({
    ...Ops,
    id: "ops.orders.review",
    method: "GET",
    path: "/v1/ops/orders/{orderId}/review",
    marks: [],
    summary: "An order's review, for operators who hide reviews that break the content rules",
    prd: ["REV-1"],
    request: { params: OrderId },
    responses: { 200: { description: "The review", schema: ReviewView.omit({ side: true, actions: true }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const hideReview = defineRoute({
    ...Ops,
    id: "ops.reviews.hide",
    method: "POST",
    path: "/v1/ops/reviews/{reviewId}/hide",
    marks: [],
    summary: "Hide a review that breaks the content rules (abuse, personal data, off topic) or the law, with a category and a reason both enterprises are told, or for a legal requirement a generic reason when the law forbids saying why; it leaves the public pages and the averages",
    prd: ["REV-1"],
    request: {
        params: z.object({ reviewId: Id }),
        body: z
            .object({
            category: ReviewHidingCategory,
            reason: z
                .string()
                .trim()
                .min(10)
                .max(500)
                .describe("Recorded; the enterprises read it unless withheld"),
            withholdReason: z
                .boolean()
                .default(false)
                .describe("Only for a legal requirement: the law forbids saying why, so the enterprises read a generic reason"),
        })
            .refine((body) => !body.withholdReason || body.category === "legal_requirement", {
            path: ["withholdReason"],
            message: "Only a legal requirement may withhold its reason",
        }),
    },
    responses: { 200: { description: "The review", schema: ReviewView.omit({ side: true, actions: true }) } },
    errors: [
        "AUTH_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "VERSION_CONFLICT",
    ],
});
export const reviewRoutes = [
    createReview,
    getOrderReview,
    replyToReview,
    listShopReviews,
    listPublicShopReviews,
    getOpsOrderReview,
    hideReview,
];
//# sourceMappingURL=review.routes.js.map